/**
 * Performance API Service
 * 
 * Handles search performance data from GSC and Bing.
 * Migrated from seo-engine-webtools-customer-module
 */

import customerApi, { USE_MOCK_DATA } from './customerApi';
import { PerformanceDataResponse } from '@/types/customer';

// Helper to convert snake_case to camelCase
function toCamelCase(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(toCamelCase);
  } else if (obj !== null && typeof obj === 'object') {
    return Object.keys(obj).reduce((acc, key) => {
      const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      acc[camelKey] = toCamelCase(obj[key]);
      return acc;
    }, {} as any);
  }
  return obj;
}

// Mock data for development
const performanceMockData: PerformanceDataResponse = {
  siteId: 1,
  dateRange: { startDate: '2026-01-01', endDate: '2026-01-22' },
  totals: {
    clicks: 45230,
    impressions: 1250000,
    ctr: 0.0362,
    avgPosition: 8.5,
  },
  rows: [
    { date: '2026-01-15', dimensions: { query: 'seo tools', page: '/tools' }, metrics: { clicks: 82, impressions: 1821, ctr: 0.045, avgPosition: 3.2 } },
    { date: '2026-01-16', dimensions: { query: 'keyword research', page: '/features' }, metrics: { clicks: 75, impressions: 1534, ctr: 0.049, avgPosition: 4.1 } },
    { date: '2026-01-17', dimensions: { query: 'rank tracking', page: '/tracking' }, metrics: { clicks: 68, impressions: 1417, ctr: 0.048, avgPosition: 5.3 } },
    { date: '2026-01-18', dimensions: { query: 'site audit', page: '/audit' }, metrics: { clicks: 92, impressions: 1804, ctr: 0.051, avgPosition: 4.8 } },
    { date: '2026-01-19', dimensions: { query: 'backlink analysis', page: '/backlinks' }, metrics: { clicks: 85, impressions: 1667, ctr: 0.051, avgPosition: 6.2 } },
    { date: '2026-01-20', dimensions: { query: 'seo tools', page: '/tools' }, metrics: { clicks: 79, impressions: 1589, ctr: 0.050, avgPosition: 3.5 } },
    { date: '2026-01-21', dimensions: { query: 'keyword research', page: '/features' }, metrics: { clicks: 88, impressions: 1742, ctr: 0.051, avgPosition: 4.0 } },
  ],
  comparison: {
    previousPeriodTotals: { clicks: 42100, impressions: 1180000, ctr: 0.0357, avgPosition: 8.8 },
    changes: { clicksChange: 7.4, impressionsChange: 5.9, ctrChange: 1.4, positionChange: -3.4 },
  },
};

export interface GetPerformanceParams {
  startDate: string;
  endDate: string;
  engineTypes?: string[];
  dimensions?: ('query' | 'page' | 'country' | 'device')[];
  limit?: number;
  page?: number;
  size?: number;
}

export interface DailyAggregatesResponse {
  dailyMetrics: Array<{
    date: string;
    clicks: number;
    impressions: number;
    avgCtr: number;
    avgPosition: number;
  }>;
  aggregates: {
    totalClicks: number;
    totalImpressions: number;
    avgCtr: number;
    avgPosition: number;
  };
}

/**
 * Get daily aggregated performance data for charts (no pagination, grouped by date only)
 */
export async function getDailyAggregates(
  siteId: number,
  startDate: string,
  endDate: string,
  device?: string,
  country?: string
): Promise<DailyAggregatesResponse> {
  const queryParams = new URLSearchParams({
    startDate,
    endDate,
  });

  if (device) queryParams.set('device', device);
  if (country) queryParams.set('country', country);

  const response = await customerApi.get<{ success: boolean; data: DailyAggregatesResponse }>(
    `/earlystage/sites/${siteId}/performance/daily-aggregates?${queryParams}`
  );
  
  return response.data.data;
}

/**
 * Get search performance data for a site
 */
export async function getPerformanceData(
  siteId: number,
  params: GetPerformanceParams
): Promise<PerformanceDataResponse> {
  if (USE_MOCK_DATA) {
    await new Promise((resolve) => setTimeout(resolve, 500));
    return performanceMockData;
  }

  const queryParams = new URLSearchParams({
    startDate: params.startDate,
    endDate: params.endDate,
  });

  if (params.engineTypes?.length) {
    queryParams.set('tools', params.engineTypes.join(','));
  }
  if (params.dimensions?.length) {
    queryParams.set('dimensions', params.dimensions.join(','));
  }
  if (params.limit) {
    queryParams.set('limit', params.limit.toString());
  }
  if (params.page !== undefined) {
    queryParams.set('page', params.page.toString());
  }
  if (params.size) {
    queryParams.set('size', params.size.toString());
  }

  const response = await customerApi.get<{ success: boolean; data: any }>(
    `/webtools/sites/${siteId}/performance/daily?${queryParams}`
  );
  
  const rawData = response.data.data;
  
  // Transform backend response to match frontend expectations
  const transformedData: PerformanceDataResponse = {
    siteId,
    dateRange: {
      startDate: params.startDate,
      endDate: params.endDate,
    },
    totals: {
      clicks: rawData.aggregates?.totalClicks || 0,
      impressions: rawData.aggregates?.totalImpressions || 0,
      ctr: rawData.aggregates?.avgCtr || 0,
      avgPosition: rawData.aggregates?.avgPosition || 0,
    },
    rows: (rawData.items || []).map((item: any) => ({
      date: item.metricDate,
      dimensions: {
        query: item.query || '',
        page: item.url || '',
        country: item.country || '',
        device: item.device || '',
        searchAppearance: item.searchAppearance || '',
        tool: item.toolId || '',
      },
      metrics: {
        clicks: item.clicks || 0,
        impressions: item.impressions || 0,
        ctr: item.ctr || 0,
        avgPosition: item.position || 0,
      },
    })),
  };
  
  return transformedData;
}

export default {
  getPerformanceData,
  getDailyAggregates,
};
